/* eslint-disable static-service/require-nltext-is-translated */
import "../../stylesheets/about-package/about.less";
import "../../stylesheets/shared-package/shared.less";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import DonateLink from "#page-package/donate-link.tsx";

import {withStrings} from "./locales/cookie-page/load-strings.tsx";

import caretDownIcon from "@phosphor-icons/core/bold/caret-down-bold.svg";

type Props = Record<any, any>;
type State = {
    cookieTypeOpen: any;
};

// We need either isMobile or isDesktop to be true at the same time. In Chrome,
// at non-standard zoom levels (e.g., 110%), [isMobile, isDesktop] =
// media.screen.breakAtWidth results in isMobile and isDesktop not being
// opposites.
export const isMobile: any = "@media screen and (max-width: 1023px)";
export const isDesktop: any = "@media screen and (min-width: 1023.5px)";

class CookiePage extends React.Component<Props, State> {
    state: State = {
        cookieTypeOpen: [false, false, false, false],
    };

    toggleCookieTypeOpen(index: any) {
        const newList = this.state.cookieTypeOpen;
        newList[index] = !newList[index];
        this.setState({cookieTypeOpen: newList});
    }

    render(): React.ReactNode {
        return (
            <PageRoot settings={{title: t`Cookie Policy`}}>
                <div className={css(styles.wrapper)}>
                    <div className={css(styles.header)}>
                        <div className={css(styles.headerCopy)}>
                            <h1 className={css(styles.pageTitle)}>
                                Khan Academy Cookie Notice
                            </h1>
                            <p className={css(styles.lastUpdated)}>
                                Last Updated: February 22nd 2024
                            </p>
                        </div>
                    </div>
                    <div className={css(styles.body)}>
                        <div className={css(styles.columns)}>
                            <div className={css(styles.callOutSection)}>
                                <p className={css(styles.callOutParagraph)}>
                                    This Cookie Notice explains how Khan Academy
                                    uses cookies on our website at
                                    www.khanacademy.org, our mobile application,
                                    and online service (collectively, our
                                    “Service”), and your related choices. Please
                                    refer to our{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="https://www.khanacademy.org/about/privacy-policy"
                                    >
                                        Privacy Policy
                                    </Link>{" "}
                                    for more information on our privacy
                                    practices.
                                </p>
                            </div>
                            <div className={css(styles.contentColumn)}>
                                <h2 className={css(styles.sectionTitle)}>
                                    What types of cookies and similar
                                    technologies does Khan Academy use?
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    To provide a personalized learning and
                                    high-quality experience for our users, we
                                    may use cookies and similar technologies
                                    that automatically collect certain
                                    information from your browser or device when
                                    you visit our website, read our emails, use
                                    our Service or otherwise engage with us.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    A cookie is a piece of data contained in a
                                    very small text file that is stored in your
                                    browser or elsewhere in your hard drive. We
                                    use cookies, web beacons, log files, and a
                                    variety of similar technologies
                                    (collectively, “cookies”) to collect
                                    information from your browser or device.
                                    These technologies collect information about
                                    how you use the Service (e.g., the pages you
                                    view, the links you click, and other actions
                                    you take on the Service), information about
                                    your browser and online usage patterns
                                    (e.g., Internet Protocol (“IP”) address,
                                    browser type, browser language, referring /
                                    exit pages and URLs, pages viewed, whether
                                    you opened an email, links clicked), and
                                    information about the device(s) you use to
                                    access the Service (e.g., mobile device
                                    identifier, mobile carrier, device type,
                                    model and manufacturer, mobile device
                                    operating system brand and model, and
                                    whether you access the Service from multiple
                                    devices).
                                </p>
                                <h2 className={css(styles.sectionTitle)}>
                                    Why do we use these technologies?
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    We may use the information collected through
                                    these technologies to better display our
                                    website, to save you time, to provide better
                                    technical support, for promotional purposes,
                                    and to measure and analyze website usage.
                                    For example, these technologies help us to:
                                </p>
                                <ol className={css(styles.orderedList)}>
                                    <li>
                                        Keep track of whether you are signed in
                                        or have previously signed in so that we
                                        can display all the features that are
                                        available to you.
                                    </li>
                                    <li>
                                        Remember your settings on the pages you
                                        visit, so that we can display your
                                        preferred content the next time you
                                        visit.
                                    </li>
                                    <li>
                                        Customize the function and appearance of
                                        the pages you visit based on information
                                        relating to your account; for example,
                                        in order to default you to a particular
                                        study level, or to remember customized
                                        settings for a report.
                                    </li>
                                    <li>
                                        Measure and analyze website usage for
                                        various purposes including providing and
                                        enhancing the Service.
                                    </li>
                                </ol>
                            </div>
                        </div>
                    </div>
                    <div className={css(styles.tableContainer)}>
                        <div className={css(styles.table)}>
                            <table>
                                <tbody>
                                    <tr>
                                        <td className={css(styles.tableHeader)}>
                                            <p
                                                className={css(
                                                    styles.paragraph,
                                                )}
                                            >
                                                <strong>Cookie Type</strong>
                                            </p>
                                        </td>
                                        <td className={css(styles.tableHeader)}>
                                            <p
                                                className={css(
                                                    styles.paragraph,
                                                )}
                                            >
                                                <strong>
                                                    Purpose and Examples
                                                </strong>
                                            </p>
                                        </td>
                                        <td className={css(styles.tableHeader)}>
                                            <p
                                                className={css(
                                                    styles.paragraph,
                                                )}
                                            >
                                                <strong>
                                                    Management Settings
                                                </strong>
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                <strong>
                                                    Strictly Necessary
                                                </strong>
                                            </p>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                (also known as Essential or
                                                Required Cookies)
                                            </p>
                                            <br />
                                            <br />
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                Certain cookies and other
                                                technologies are essential in
                                                order to enable our Service to
                                                provide the features you have
                                                requested, such as making it
                                                possible for you to access our
                                                product and information related
                                                to your account, and to ensure
                                                that we maintain data
                                                consistency and integrity for
                                                your learning activity.
                                            </p>
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                For example, each time you log
                                                into our Service, a Strictly
                                                Necessary Cookie authenticates
                                                that it is you logging in and
                                                allows you to use the Service
                                                without having to re-enter your
                                                password when you visit a new
                                                page or new unit during your
                                                browsing session. All of our
                                                essential cookies are
                                                first-party cookies.
                                            </p>
                                            <br />
                                            <br />
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                Strictly Necessary Cookies are
                                                necessary to operate our
                                                websites, and can’t be switched
                                                off. You can set your browser to
                                                block (or alert you about) these
                                                Strictly Necessary Cookies, but
                                                some parts of the Service won’t
                                                work as a result.
                                            </p>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                <strong>Functional</strong>
                                            </p>
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                These cookies provide you with a
                                                more tailored experience and
                                                allow you to make certain
                                                selections on our Service. For
                                                example, these cookies store
                                                information such as your
                                                preferred language and website
                                                preferences.
                                            </p>
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                Opting out of Functional Cookies
                                                may affect the functionality of
                                                the Service. In other words,
                                                certain features of the Service
                                                depend on cookies – so if you
                                                block all cookies, you may not
                                                be able to use all aspects of
                                                the Service.
                                            </p>
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                You can manage functional
                                                cookies in our{" "}
                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                                <Link
                                                    inline={true}
                                                    href="#"
                                                    onClick={(
                                                        e: React.SyntheticEvent,
                                                    ) => {
                                                        e.preventDefault();
                                                        // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                        window.Optanon.ToggleInfoDisplay();
                                                    }}
                                                >
                                                    cookie preference center
                                                </Link>
                                                . You can also use your browser
                                                settings to opt-out of
                                                Functional Cookies. For more
                                                information on how to do that,
                                                see{" "}
                                                <Link
                                                    inline={true}
                                                    target="_blank"
                                                    href="http://www.allaboutcookies.org/"
                                                >
                                                    All About Cookies
                                                </Link>
                                                .
                                            </p>
                                            <br />
                                            <br />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                <strong>Performance</strong>
                                            </p>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                (also known as Analytic)
                                            </p>
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                These cookies and other
                                                technologies allow us to
                                                understand how you interact with
                                                our Service (e.g., how often you
                                                use our Service, where you are
                                                accessing the Service from and
                                                the content that you’re
                                                interacting with). Analytic
                                                cookies enable us to support and
                                                improve how our Service
                                                operates.
                                            </p>
                                            <br />
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                For example, we use Google
                                                Analytics cookies to help us
                                                measure traffic and usage trends
                                                for the Service, and to
                                                understand more about the
                                                demographics of our users.
                                            </p>
                                            <br />
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                We also may use web beacons to
                                                gauge the effectiveness of
                                                certain communications and the
                                                effectiveness of our marketing
                                                campaigns via HTML emails.
                                            </p>
                                            <br />
                                            <br />
                                            <br />
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                If you do not allow Analytic
                                                Cookies, then we won’t know when
                                                you have visited our Service and
                                                we won’t be able to monitor its
                                                performance.
                                            </p>
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                You can manage performance
                                                cookies in our{" "}
                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                                <Link
                                                    inline={true}
                                                    href="#"
                                                    onClick={(
                                                        e: React.SyntheticEvent,
                                                    ) => {
                                                        e.preventDefault();
                                                        // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                        window.Optanon.ToggleInfoDisplay();
                                                    }}
                                                >
                                                    cookie preference center
                                                </Link>
                                                .
                                            </p>
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                For information on how to
                                                opt-out of Google Analytics,
                                                please see{" "}
                                                <Link
                                                    inline={true}
                                                    target="_blank"
                                                    href="https://tools.google.com/dlpage/gaoptout"
                                                >
                                                    Google’s opt-out options
                                                    options
                                                </Link>{" "}
                                                and{" "}
                                                <Link
                                                    inline={true}
                                                    target="_blank"
                                                    href="http://www.google.com/policies/privacy/partners"
                                                >
                                                    Google’s privacy practices
                                                </Link>
                                                .
                                            </p>
                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                You may set your email options
                                                to prevent the automatic
                                                downloading of images that may
                                                contain technologies that would
                                                allow us to know whether you
                                                have accessed our email and
                                                interacted with the content. For
                                                example, web beacons in emails
                                                may be blocked by setting your
                                                email preferences to “Plain Text
                                                Only”.
                                            </p>
                                            <br />
                                            <br />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                <strong>Targeting</strong>
                                            </p>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                (also known as Tracking)
                                            </p>
                                            <br />
                                            <br />
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                These cookies are used on a
                                                limited basis, only on pages
                                                directed to adults (teachers,
                                                donors, or parents). We use
                                                these cookies to inform our own
                                                digital marketing and help us
                                                connect with people who are
                                                interested in our Service and
                                                our mission.
                                            </p>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                We do not use cookies to serve
                                                third party ads on our Service.
                                            </p>
                                        </td>
                                        <td className={css(styles.tableData)}>
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                For general information on
                                                managing Targeting Cookie
                                                opt-outs, please see{" "}
                                                <Link
                                                    inline={true}
                                                    href="#your-choices"
                                                >
                                                    Your Choices
                                                </Link>
                                                .
                                            </p>
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                You can manage targeting cookies
                                                in our{" "}
                                                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                                <Link
                                                    inline={true}
                                                    href="#"
                                                    onClick={(
                                                        e: React.SyntheticEvent,
                                                    ) => {
                                                        e.preventDefault();
                                                        // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                        window.Optanon.ToggleInfoDisplay();
                                                    }}
                                                >
                                                    cookie preference center
                                                </Link>
                                                .
                                            </p>
                                            <br />
                                            <br />
                                            <p
                                                className={css(
                                                    styles.tableParagraph,
                                                )}
                                            >
                                                We have also provided links to
                                                opt-out of these marketing and
                                                digital content messages. The
                                                Tracking Cookies listed below
                                                are examples and subject to
                                                change.
                                            </p>
                                            <ul className={css(styles.list)}>
                                                <li>
                                                    Google:{" "}
                                                    <Link
                                                        inline={true}
                                                        target="_blank"
                                                        href="http://www.google.com/policies/technologies/ads/"
                                                    >
                                                        Google opt-out
                                                    </Link>
                                                </li>
                                                <li>
                                                    Facebook:{" "}
                                                    <Link
                                                        inline={true}
                                                        target="_blank"
                                                        href="https://www.facebook.com/help/568137493302217"
                                                    >
                                                        Facebook opt-out
                                                    </Link>
                                                </li>
                                                <li>
                                                    LinkedIn:{" "}
                                                    <Link
                                                        inline={true}
                                                        target="_blank"
                                                        href="https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out"
                                                    >
                                                        LinkedIn opt-out
                                                    </Link>
                                                </li>
                                                <li>
                                                    Twitter:{" "}
                                                    <Link
                                                        inline={true}
                                                        target="_blank"
                                                        href="https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads"
                                                    >
                                                        Twitter opt-out
                                                    </Link>
                                                </li>
                                            </ul>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className={css(styles.mobileTable)}>
                        <div className={css(styles.mobileTableHeader)}>
                            Cookie Type
                        </div>
                        <div className={css(styles.mobileTableSection)}>
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
                            <div
                                onClick={() => this.toggleCookieTypeOpen(0)}
                                className={css(styles.dropdownHeader)}
                            >
                                <p className={css(styles.mobileSectionTitle)}>
                                    Strictly Necessary <br />
                                    (also known as Essential or Required
                                    Cookies)
                                </p>
                                <PhosphorIcon
                                    icon={caretDownIcon}
                                    color={color.offBlack}
                                    size="small"
                                    style={{
                                        transition: `transform 0.3s`,
                                        transform: `rotate(${
                                            this.state.cookieTypeOpen[0]
                                                ? "-180deg"
                                                : "0deg"
                                        })`,
                                    }}
                                />
                            </div>
                            {this.state.cookieTypeOpen[0] && (
                                <div>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Purpose and Examples
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        Certain cookies and other technologies
                                        are essential in order to enable our
                                        Service to provide the features you have
                                        requested, such as making it possible
                                        for you to access our product and
                                        information related to your account.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        For example, each time you log into our
                                        Service, a Strictly Necessary Cookie
                                        authenticates that it is you logging in
                                        and allows you to use the Service
                                        without having to re-enter your password
                                        when you visit a new page or new unit
                                        during your browsing session.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Management Settings
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        Strictly Necessary Cookies are necessary
                                        to operate our websites, and can’t be
                                        switched off. You can set your browser
                                        to block (or alert you about) these
                                        Strictly Necessary Cookies, but some
                                        parts of the Service won’t work as a
                                        result.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className={css(styles.mobileTableSection)}>
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
                            <div
                                onClick={() => this.toggleCookieTypeOpen(1)}
                                className={css(styles.dropdownHeader)}
                            >
                                <p className={css(styles.mobileSectionTitle)}>
                                    Functional
                                </p>
                                <PhosphorIcon
                                    icon={caretDownIcon}
                                    color={color.offBlack}
                                    size="small"
                                    style={{
                                        transition: `transform 0.3s`,
                                        transform: `rotate(${
                                            this.state.cookieTypeOpen[1]
                                                ? "-180deg"
                                                : "0deg"
                                        })`,
                                    }}
                                />
                            </div>
                            {this.state.cookieTypeOpen[1] && (
                                <div>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Purpose and Examples
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        These cookies provide you with a more
                                        tailored experience and allow you to
                                        make certain selections on our Service.
                                        For example, these cookies store
                                        information such as your preferred
                                        language and website preferences.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Management Settings
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        Opting out of Functional Cookies may
                                        affect the functionality of the Service.
                                        In other words, certain features of the
                                        Service depend on cookies – so if you
                                        block all cookies, you may not be able
                                        to use all aspects of the Service.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        You can manage functional cookies in our{" "}
                                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                        <Link
                                            inline={true}
                                            href="#"
                                            onClick={(
                                                e: React.SyntheticEvent,
                                            ) => {
                                                e.preventDefault();
                                                // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                window.Optanon.ToggleInfoDisplay();
                                            }}
                                        >
                                            cookie preference center
                                        </Link>
                                        . You can also use your browser settings
                                        to opt-out of Functional Cookies. For
                                        more information on how to do that, see{" "}
                                        <Link
                                            inline={true}
                                            target="_blank"
                                            href="http://www.allaboutcookies.org/"
                                        >
                                            All About Cookies
                                        </Link>
                                        .
                                    </p>
                                </div>
                            )}
                        </div>
                        <div className={css(styles.mobileTableSection)}>
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
                            <div
                                onClick={() => this.toggleCookieTypeOpen(2)}
                                className={css(styles.dropdownHeader)}
                            >
                                <p className={css(styles.mobileSectionTitle)}>
                                    Performance <br />
                                    (also known as Analytic)
                                </p>
                                <PhosphorIcon
                                    icon={caretDownIcon}
                                    color={color.offBlack}
                                    size="small"
                                    style={{
                                        transition: `transform 0.3s`,
                                        transform: `rotate(${
                                            this.state.cookieTypeOpen[2]
                                                ? "-180deg"
                                                : "0deg"
                                        })`,
                                    }}
                                />
                            </div>
                            {this.state.cookieTypeOpen[2] && (
                                <div>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Purpose and Examples
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        These cookies and other technologies
                                        allow us to understand how you interact
                                        with our Service (e.g., how often you
                                        use our Service, where you are accessing
                                        the Service from and the content that
                                        you’re interacting with). Analytic
                                        cookies enable us to support and improve
                                        how our Service operates.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        For example, we use Google Analytics
                                        cookies to help us measure traffic and
                                        usage trends for the Service, and to
                                        understand more about the demographics
                                        of our users.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        We also may use web beacons to gauge the
                                        effectiveness of certain communications
                                        and the effectiveness of our marketing
                                        campaigns via HTML emails.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Management Settings
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        If you do not allow Analytic Cookies,
                                        then we won’t know when you have visited
                                        our Service and we won’t be able to
                                        monitor its performance.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        You can manage performance cookies in
                                        our{" "}
                                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                        <Link
                                            inline={true}
                                            href="#"
                                            onClick={(
                                                e: React.SyntheticEvent,
                                            ) => {
                                                e.preventDefault();
                                                // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                window.Optanon.ToggleInfoDisplay();
                                            }}
                                        >
                                            cookie preference center
                                        </Link>
                                        .
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        For information on how to opt-out of
                                        Google Analytics, please see{" "}
                                        <Link
                                            inline={true}
                                            target="_blank"
                                            href="https://tools.google.com/dlpage/gaoptout"
                                        >
                                            Google’s opt-out options
                                        </Link>{" "}
                                        and{" "}
                                        <Link
                                            inline={true}
                                            target="_blank"
                                            href="http://www.google.com/policies/privacy/partners"
                                        >
                                            Google’s privacy practices
                                        </Link>
                                        .
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        You may set your email options to
                                        prevent the automatic downloading of
                                        images that may contain technologies
                                        that would allow us to know whether you
                                        have accessed our email and interacted
                                        with the content. For example, web
                                        beacons in emails may be blocked by
                                        setting your email preferences to “Plain
                                        Text Only”.
                                    </p>
                                </div>
                            )}
                        </div>
                        <div
                            className={css(styles.lastChildMobileTableSection)}
                        >
                            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- TODO: Address a11y error */}
                            <div
                                onClick={() => this.toggleCookieTypeOpen(3)}
                                className={css(styles.dropdownHeader)}
                            >
                                <p className={css(styles.mobileSectionTitle)}>
                                    Targeting <br />
                                    (also known as Tracking)
                                </p>
                                <PhosphorIcon
                                    icon={caretDownIcon}
                                    color={color.offBlack}
                                    size="small"
                                    style={{
                                        transition: `transform 0.3s`,
                                        transform: `rotate(${
                                            this.state.cookieTypeOpen[3]
                                                ? "-180deg"
                                                : "0deg"
                                        })`,
                                    }}
                                />
                            </div>
                            {this.state.cookieTypeOpen[3] && (
                                <div>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Purpose and Examples
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        These cookies are used on a limited
                                        basis, only on pages directed to adults
                                        (teachers, donors, or parents). We use
                                        these cookies to inform our own digital
                                        marketing and help us connect with
                                        people who are interested in our Service
                                        and our mission.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        We do not use cookies to serve third
                                        party ads on our Service.
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionTitle,
                                        )}
                                    >
                                        Management Settings
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        For general information on managing
                                        Targeting Cookie opt-outs, please see{" "}
                                        <Link
                                            inline={true}
                                            href="#your-choices"
                                        >
                                            Your Choices
                                        </Link>
                                        .
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        You can manage targeting cookies in our{" "}
                                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                        <Link
                                            inline={true}
                                            href="#"
                                            onClick={(
                                                e: React.SyntheticEvent,
                                            ) => {
                                                e.preventDefault();
                                                // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                                window.Optanon.ToggleInfoDisplay();
                                            }}
                                        >
                                            cookie preference center
                                        </Link>
                                        .
                                    </p>
                                    <p
                                        className={css(
                                            styles.mobileSectionParagraph,
                                        )}
                                    >
                                        We have also provided links to opt-out
                                        of these marketing and digital content
                                        messages. The Tracking Cookies listed
                                        below are examples and subject to
                                        change.
                                    </p>
                                    <ul className={css(styles.mobileList)}>
                                        <li>
                                            Google:{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="http://www.google.com/policies/technologies/ads/"
                                            >
                                                Google opt-out
                                            </Link>
                                        </li>
                                        <li>
                                            Facebook:{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="https://www.facebook.com/help/568137493302217"
                                            >
                                                Facebook opt-out
                                            </Link>
                                        </li>
                                        <li>
                                            LinkedIn:{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="https://www.linkedin.com/psettings/guest-controls/retargeting-opt-out"
                                            >
                                                LinkedIn opt-out
                                            </Link>
                                        </li>
                                        <li>
                                            Twitter:{" "}
                                            <Link
                                                inline={true}
                                                target="_blank"
                                                href="https://help.twitter.com/en/safety-and-security/privacy-controls-for-tailored-ads"
                                            >
                                                Twitter opt-out
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={css(styles.body)}>
                        <div className={css(styles.columns)}>
                            <div className={css(styles.callOutSection)}>
                                <p className={css(styles.callOutParagraph)}>
                                    <strong>Updates to this Notice</strong>
                                </p>
                                <p className={css(styles.updateParagraph)}>
                                    This Cookie Notice may be updated from time
                                    to time. If we make any changes, we will
                                    notify you by revising the “Effective Date”
                                    at the top of this Cookie Notice.
                                </p>
                            </div>
                            <div className={css(styles.contentColumn)}>
                                <h2
                                    id="your-choices"
                                    className={css(styles.sectionTitle)}
                                >
                                    Your Choices
                                </h2>
                                <p className={css(styles.paragraph)}>
                                    You can view and adjust your cookie
                                    preferences in our{" "}
                                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- TODO: Address a11y error */}
                                    <Link
                                        inline={true}
                                        href="#"
                                        onClick={(e: React.SyntheticEvent) => {
                                            e.preventDefault();
                                            // @ts-expect-error - TS2339 - Property 'Optanon' does not exist on type 'Window'.
                                            window.Optanon.ToggleInfoDisplay();
                                        }}
                                    >
                                        cookie preference center
                                    </Link>
                                    . You can choose to accept or decline all
                                    cookies or specific types of cookies, other
                                    than strictly necessary cookies. The cookie
                                    manager will block or disable cookies once
                                    your preference has been set, but will not
                                    result in removal of cookies placed prior to
                                    your election.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    You also may be able to refuse or disable
                                    cookies by adjusting your web browser
                                    settings. Some browsers have options that
                                    allow the visitor to control whether the
                                    browser will accept cookies, reject cookies,
                                    or notify the visitor each time a cookie is
                                    sent. Because each web browser is different,
                                    please consult the instructions provided by
                                    your web browser (typically in the “help”
                                    section).
                                </p>
                                <p className={css(styles.paragraph)}>
                                    If you choose to refuse, disable, or delete
                                    these technologies, some of the
                                    functionality of the Services may no longer
                                    be available to you. And deleting cookies
                                    may in some cases cancel the opt-out
                                    selection in your browser. Although most
                                    browsers and devices accept cookies by
                                    default, you can usually manage settings to
                                    clear or decline cookies (for instance, by
                                    using a “private” or “incognito” mode). If
                                    you disable cookies, however, some of the
                                    features of our services may not function
                                    properly.
                                </p>
                                <p className={css(styles.paragraph)}>
                                    Certain pages on our website are hosted by
                                    third-party platforms. When you visit these
                                    pages, cookies might be set by these third
                                    parties. We do not control the placement or
                                    management of these cookies, and your cookie
                                    preferences may not be recognized by the
                                    third party. You will need to manage or
                                    adjust your cookie settings with those
                                    parties directly. Third party hosting occurs
                                    on a limited number of pages, such as{" "}
                                    <DonateLink
                                        inline={true}
                                        urlParams={{
                                            c_src2: "Webapp.cookiePolicy",
                                        }}
                                    >
                                        donate.khanacademy.org
                                    </DonateLink>
                                    ,{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://blog.khanacademy.org/"
                                    >
                                        blog.khanacademy.org
                                    </Link>
                                    ,{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://shop.khanacademy.org/"
                                    >
                                        shop.khanacademy.org
                                    </Link>
                                    , and{" "}
                                    <Link
                                        target="_blank"
                                        inline={true}
                                        href="https://learn.khanacademy.org/khan-academy-kids/"
                                    >
                                        learn.khanacademy.org
                                    </Link>{" "}
                                    (containing information about our Khan
                                    Academy Kids app).
                                </p>
                                <p className={css(styles.paragraph)}>
                                    To learn more about how to opt-out of ad
                                    targeting (and Targeting Cookies), you can
                                    go to the{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="http://www.networkadvertising.org/choices/"
                                    >
                                        Network Advertising Initiative (NAI)
                                    </Link>
                                    , the{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="http://www.aboutads.info/choices/"
                                    >
                                        Digital Advertising Alliance&rsquo;s
                                        Consumer Choice (DAA)
                                    </Link>
                                    ,{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="http://www.youronlinechoices.eu/"
                                    >
                                        Your Online Choices (EDAA)
                                    </Link>{" "}
                                    if you are in the European Economic Area,{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="https://youradchoices.ca/en/tools"
                                    >
                                        AdChoices (Canada)
                                    </Link>{" "}
                                    if you are in Canada, or{" "}
                                    <Link
                                        inline={true}
                                        target="_blank"
                                        href="https://youronlinechoices.com.au/"
                                    >
                                        AdChoices Australia
                                    </Link>{" "}
                                    if you are in Australia. If you use these
                                    tools, you will need to opt-out separately
                                    for each of your devices and for each web
                                    browser on each device. You may also be able
                                    to limit interest-based advertising through
                                    the settings on your mobile device by
                                    selecting “limit ad tracking” (iOS) or
                                    “opt-out of interest based ads” (Android).
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </PageRoot>
        );
    }
}

export default withStrings(CookiePage);

const styles = StyleSheet.create({
    wrapper: {
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        fontSize: 16,
        [mediaQueries.smOrSmaller]: {
            fontSize: 14,
        },
    },
    header: {
        height: 300,
        width: "100vw",
        display: "flex",
        backgroundPosition: "center",
        backgroundImage: cssUrl("/images/cookie_notice_header_desktop.jpg"),
        alignItems: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        [mediaQueries.smOrSmaller]: {
            height: 0,
            paddingTop: "75%",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            width: "100vw",
            backgroundImage: cssUrl("/images/cookie_notice_header_mobile.jpg"),
        },
    },
    headerCopy: {
        display: "flex",
        flexDirection: "column",
        maxWidth: "55%",
        marginLeft: "5%",
        [mediaQueries.smOrSmaller]: {
            marginTop: "-70%",
            maxWidth: "60%",
            marginLeft: "20%",
            justifyContent: "center",
        },
    },
    pageTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 42,
        fontWeight: 600,
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            fontSize: 35,
            textAlign: "center",
        },
    },
    lastUpdated: {
        fontSize: 18,
        fontStyle: "italic",
        fontWeight: 700,
        lineHeight: "30px",
        color: color.darkBlue,
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    body: {
        flex: 1,
        display: "flex",
        justifyContent: "center",
        padding: "40px 0",
    },
    columns: {
        display: "flex",
        maxWidth: "1200px",
        fontSize: 16,
        [mediaQueries.smOrSmaller]: {
            flexDirection: "column",
            fontSize: 14,
        },
    },
    callOutSection: {
        display: "block",
        fontSize: 18,
        fontWeight: 700,
        marginRight: 25,
        backgroundColor: "#F4EEE2",
        [mediaQueries.smOrSmaller]: {
            margin: "0 4%",
            fontSize: 14,
        },
        [mediaQueries.mdOrLarger]: {
            maxHeight: 380,
            display: "flex",
            flex: 1,
            flexDirection: "column",
        },
    },
    callOutParagraph: {
        backgroundColor: "#F4EEE2",
        padding: "8%",
        fontSize: 18,
        lineHeight: "32px",
    },
    updateParagraph: {
        backgroundColor: "#F4EEE2",
        padding: "10% 8%",
        fontSize: 18,
        fontWeight: "normal",
        lineHeight: "32px",
        marginTop: -55,
    },
    contentColumn: {
        flex: 2,
        display: "flex",
        flexDirection: "column",
        [mediaQueries.smOrSmaller]: {
            padding: "5%",
        },
    },
    tableContainer: {
        display: "flex",
        justifyContent: "center",
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    table: {
        display: "flex",
        flexDirection: "column",
        maxWidth: 1200,
        fontSize: 16,
    },
    tableHeader: {
        backgroundColor: "#B6D2DA",
        textAlign: "center",
        fontSize: 18,
        lineHeight: "32px",
        border: "1px solid #C4C4C4",
    },
    tableData: {
        width: "33%",
        padding: "2%",
        border: "1px solid #C4C4C4",
    },
    paragraph: {
        fontSize: 18,
        lineHeight: "32px",
    },
    tableParagraph: {
        fontSize: 18,
        lineHeight: "32px",
        margin: 0,
    },
    orderedList: {
        listStyle: "normal",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 20,
    },
    list: {
        listStyle: "disc",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 18,
        marginBottom: 20,
    },
    mobileList: {
        listStyle: "disc",
        marginLeft: 20,
        lineHeight: "32px",
        fontSize: 14,
        marginBottom: 20,
    },
    sectionTitle: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 34,
        fontWeight: "normal",
        margin: 0,
        [mediaQueries.smOrSmaller]: {
            fontSize: 24,
        },
    },
    mobileTable: {
        margin: "-2% 6%",
        [mediaQueries.mdOrLarger]: {
            display: "none",
        },
    },
    mobileTableHeader: {
        backgroundColor: "#B6D2DA",
        textAlign: "center",
        padding: "2%",
        fontWeight: 700,
    },
    mobileTableSection: {
        padding: "6%",
        border: "1px solid #C4C4C4",
        borderBottom: 0,
    },
    lastChildMobileTableSection: {
        padding: "6%",
        border: "1px solid #C4C4C4",
    },
    mobileSectionTitle: {
        fontSize: 14,
        fontWeight: 700,
        lineHeight: "24px",
        margin: 0,
    },
    mobileSectionParagraph: {
        fontSize: 14,
        lineHeight: "24px",
        fontWeight: 400,
        margin: 0,
        marginBottom: 22,
    },
    dropdownHeader: {
        display: "flex",
        justifyContent: "space-between",
        flexDirection: "row",
        alignItems: "center",
        margin: "22px 0",
    },
});
